import { useCallback } from "react";

import { useTranslation } from "next-i18next";

import {
  getFacetLabelKey,
  getFacetValueLabelKey,
  humanizeFacetKey,
} from "@/features/search/utils/facet-label.utils";

/**
 * Resolve a facet key to its display label. Falls back to a humanized key when
 * no translation exists, so a facet the API adds tomorrow still renders.
 */
export const useFacetLabel = () => {
  const { t, i18n } = useTranslation();

  return useCallback(
    (facetKey: string) => {
      const key = getFacetLabelKey(facetKey);
      return i18n.exists(key) ? t(key) : humanizeFacetKey(facetKey);
    },
    [t, i18n]
  );
};

/**
 * Resolve one facet value to its display label.
 *
 * Almost every value arrives labelled by the service. The exception is an opt-in boolean, whose only
 * row is a bare `true` — without this it renders literally as "true", in the panel and in the chip
 * above the results.
 */
export const useFacetValueLabel = () => {
  const { t, i18n } = useTranslation();

  return useCallback(
    (facetKey: string, value: { value: string; label?: string | null }) => {
      const key = getFacetValueLabelKey(facetKey);

      if (key && i18n.exists(key)) {
        return t(key);
      }

      return value.label ?? value.value;
    },
    [t, i18n]
  );
};
